import React from "react";
import { useState, useRef, useEffect } from "react";
import { useHistory, useLocation } from "react-router-dom";

import Logo from "../../../assets/Logo.svg";
import GoogleIcon from "../../../assets/google-icon-logo.svg";
import RegisterPreviewImage from "../../../assets/images/shutterstock_1092442658.jpg";
import { Typography } from "@material-ui/core";
import { Visibility, VisibilityOff } from "@material-ui/icons";
import styles from "../AuthWrapper.module.css";
import { registerUser } from "../services";

import { showToast } from "../../../utils/toast";
import { googleSignInAPI } from "../../../api/userApi";
import { useGoogleLogin } from "@react-oauth/google";
import useSEO from "../../../seo/use-seo";
import StandardPageHelmet from "../../../seo/components/StandardPageHelmet";
import { useAppSelector } from "../../../redux/typedHooks";
import { useGoogleReCaptcha } from "react-google-recaptcha-v3";
import { setUser } from "../../../redux/modules/userSlice";
import { setTokens } from "../../../utils/tokens";
import { useDispatch } from "react-redux";
import {
  trackReferralAttributedStage,
  trackSignupCompleted,
  trackSignupStarted,
} from "../../../analytics/canonicalTracking";
import { isStagingCreatorOnboardingBypassEnabled } from "../../../config/stagingCreatorOnboarding";
import {
  aliasMixpanelUser,
  identifyMixpanelUser,
  setMixpanelPeopleProfile,
} from "../../../config/mixpanel";

// Власна валідація пароля замість Firebase
interface PasswordValidationStatus {
  isValid: boolean;
  meetsMinPasswordLength?: boolean;
  meetsMaxPasswordLength?: boolean;
  containsUppercaseLetter?: boolean;
  containsLowercaseLetter?: boolean;
  containsNumericCharacter?: boolean;
  containsNonAlphanumericCharacter?: boolean;
  passwordPolicy?: {
    customStrengthOptions?: {
      minPasswordLength?: number;
      maxPasswordLength?: number;
      containsUppercaseLetter?: boolean;
      containsLowercaseLetter?: boolean;
      containsNumericCharacter?: boolean;
      containsNonAlphanumericCharacter?: boolean;
      allowedNonAlphanumericCharacters?: string;
    };
    allowedNonAlphanumericCharacters?: string;
  };
}

const isValidEmail = (email: string) => {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
};

function SignUp() {
  const seo = useSEO("register");
  // const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isPasswordChecking, setIsPasswordChecking] = useState(false);
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [errors, setErrors] = useState({
    name: "",
    email: "",
    password: "",
  });

  const dispatch = useDispatch();
  const history = useHistory();
  const location = useLocation();
  useAppSelector(state => state.user.currentUser);

  // Get referral ID from URL params
  const [referralId, setReferralId] = useState<string | null>(null);

  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const ref = searchParams.get("ref");
    if (ref) {
      setReferralId(ref);
    }
  }, [location.search]);

  const signupStartedTracked = useRef(false);
  useEffect(() => {
    if (signupStartedTracked.current) return;
    signupStartedTracked.current = true;
    const sp = new URLSearchParams(location.search);
    trackSignupStarted({
      path: location.pathname,
      user_role: "guest",
      signup_method: "email_password",
      source: sp.get("utm_source") || undefined,
      medium: sp.get("utm_medium") || undefined,
      campaign: sp.get("utm_campaign") || undefined,
    });
  }, [location.pathname, location.search]);

  // Initialize reCAPTCHA v3
  const { executeRecaptcha } = useGoogleReCaptcha();
  const passwordValidationRequestId = useRef(0);
  const [passwordValidationStatus, setPasswordValidationStatus] =
    useState<PasswordValidationStatus | null>(null);

  // Handle Enter key press
  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === "Enter" && isFormValid()) {
      handlerSubmit();
    }
  };

  // Toggle password visibility
  const togglePasswordVisibility = () => {
    setShowPassword(!showPassword);
  };

  // Validation functions
  const validateField = (field: string, value: string) => {
    let error = "";

    switch (field) {
      // case "name":
      //   if (!value.trim()) {
      //     error = "Username is required";
      //   } else if (value.length >= 16) {
      //     error = "Username must be less than 16 characters";
      //   }
      //   break;
      case "email":
        if (!value.trim()) {
          error = "Email is required";
        } else if (!isValidEmail(value)) {
          error = "Please enter a valid email address";
        }
        break;
    }

    setErrors(prev => ({ ...prev, [field]: error }));
    return error === "";
  };

  // Check if form is valid
  const isFormValid = () => {
    return (
      // isValidUsername(name) &&
      isValidEmail(email) &&
      passwordValidationStatus?.isValid &&
      !isPasswordChecking &&
      !errors.name &&
      !errors.email &&
      !errors.password
    );
  };

  const googleRegistration = useGoogleLogin({
    onSuccess: async (tokenResponse: { access_token: string }) => {
      try {
        // Get user info from Google using access token
        const userInfoResponse = await fetch(
          "https://www.googleapis.com/oauth2/v3/userinfo",
          {
            headers: {
              Authorization: `Bearer ${tokenResponse.access_token}`,
            },
          }
        );
        const userInfo = await userInfoResponse.json();

        const searchParams = new URLSearchParams(location.search);
        const ref = searchParams.get("ref");

        // Send credential to backend
        googleSignInAPI({
          google_access_token: tokenResponse.access_token,
          email: userInfo.email,
          name: userInfo.name,
          picture: userInfo.picture,
          referralId: ref || undefined,
        })
          .then(response => {
            showToast(response.data.message || "Registration successful!");
            setTokens(response.data.tokens);
            dispatch(setUser(response.data.user));
            const u = response.data.user;
            const role = u?.userType?.title?.toLowerCase?.() || "guest";
            // Make sure distinct_id is "real" before signup_completed
            aliasMixpanelUser(u?.id);
            identifyMixpanelUser(u?.id);
            setMixpanelPeopleProfile({
              email: u?.email || userInfo?.email,
              name: u?.username || userInfo?.name,
            });
            trackSignupCompleted({
              user_id: u?.id,
              user_role: role === "viewer" || role === "creator" ? role : "guest",
              signup_method: "google_oauth",
              source: new URLSearchParams(location.search).get("utm_source") || undefined,
              medium: new URLSearchParams(location.search).get("utm_medium") || undefined,
              campaign: new URLSearchParams(location.search).get("utm_campaign") || undefined,
            });
            if (ref) {
              trackReferralAttributedStage({
                referral_stage: "signup",
                referrer_user_id: ref,
                user_id: u?.id,
                user_role: role === "viewer" || role === "creator" ? role : "guest",
              });
            }
            // Зберігаємо інформацію про те, що користувач реєструвався/входив
            localStorage.setItem("hasRegisteredOrLoggedIn", "true");
            showToast("Login successful!");
            history.push("/");
          })
          .catch(err => {
            const errorMessage =
              err?.response?.data?.message ||
              err?.message ||
              "Registration API error";
            showToast(errorMessage, "error");
          });
      } catch (error: any) {
        console.error("Google registration error:", error);
        const errorMessage =
          error?.response?.data?.message ||
          error?.message ||
          "Error while signing in with Google!";
        showToast(errorMessage, "error");
      }
    },
    onError: () => {
      showToast("Error while signing in with Google!", "error");
    },
  });

  // Input change handlers
  // const handleNameChange = (e: React.ChangeEvent<HTMLInputElement>) => {
  //   const value = e.target.value;
  //   setName(value);
  //   validateField("name", value);
  // };

  const buildPasswordErrorMessage = (
    validation: PasswordValidationStatus
  ): string => {
    const messages: string[] = [];
    const options = validation.passwordPolicy?.customStrengthOptions || {};

    if (
      options.minPasswordLength &&
      validation.meetsMinPasswordLength === false
    ) {
      messages.push(
        `Password must be at least ${options.minPasswordLength} characters long.`
      );
    }
    if (
      options.maxPasswordLength &&
      validation.meetsMaxPasswordLength === false
    ) {
      messages.push(
        `Password must not exceed ${options.maxPasswordLength} characters.`
      );
    }
    if (
      options.containsUppercaseLetter &&
      validation.containsUppercaseLetter === false
    ) {
      messages.push("Password must include an uppercase letter.");
    }
    if (
      options.containsLowercaseLetter &&
      validation.containsLowercaseLetter === false
    ) {
      messages.push("Password must include a lowercase letter.");
    }
    if (
      options.containsNumericCharacter &&
      validation.containsNumericCharacter === false
    ) {
      messages.push("Password must include a number.");
    }
    if (
      options.containsNonAlphanumericCharacter &&
      validation.containsNonAlphanumericCharacter === false
    ) {
      const allowed =
        validation.passwordPolicy?.allowedNonAlphanumericCharacters || "";
      messages.push(
        allowed
          ? `Password must include a special character (${allowed}).`
          : "Password must include a special character."
      );
    }

    if (!messages.length) {
      messages.push("Password does not meet the required policy.");
    }

    return messages.join(" ");
  };

  const validatePasswordField = async (value: string): Promise<boolean> => {
    const trimmed = value.trim();

    if (!trimmed) {
      setErrors(prev => ({ ...prev, password: "Password is required" }));
      setPasswordValidationStatus(null);
      return false;
    }

    const requestId = ++passwordValidationRequestId.current;
    setIsPasswordChecking(true);

    try {
      // Власна валідація пароля замість Firebase
      const validation: PasswordValidationStatus = {
        isValid: true,
        meetsMinPasswordLength: trimmed.length >= 8,
        meetsMaxPasswordLength: trimmed.length <= 128,
        containsUppercaseLetter: /[A-Z]/.test(trimmed),
        containsLowercaseLetter: /[a-z]/.test(trimmed),
        containsNumericCharacter: /[0-9]/.test(trimmed),
        containsNonAlphanumericCharacter:
          /[!@#$%^&*()_+\-=[\]{};':"\\|,.<>/?]/.test(trimmed),
        passwordPolicy: {
          customStrengthOptions: {
            minPasswordLength: 8,
            maxPasswordLength: 128,
            containsUppercaseLetter: true,
            containsLowercaseLetter: true,
            containsNumericCharacter: true,
            containsNonAlphanumericCharacter: true,
          },
        },
      };

      // Перевіряємо всі вимоги
      validation.isValid = !!(
        validation.meetsMinPasswordLength &&
        validation.meetsMaxPasswordLength &&
        validation.containsUppercaseLetter &&
        validation.containsLowercaseLetter &&
        validation.containsNumericCharacter &&
        validation.containsNonAlphanumericCharacter
      );

      if (passwordValidationRequestId.current !== requestId) {
        return validation.isValid;
      }

      setPasswordValidationStatus(validation);

      if (validation.isValid) {
        setErrors(prev => ({ ...prev, password: "" }));
        return true;
      }

      const message = buildPasswordErrorMessage(validation);
      setErrors(prev => ({ ...prev, password: message }));
      return false;
    } catch (error) {
      console.error("Password validation error:", error);
      const fallbackMessage =
        trimmed.length < 8
          ? "Password must be at least 8 characters long."
          : "Unable to validate password right now. Please try again.";
      setErrors(prev => ({ ...prev, password: fallbackMessage }));
      setPasswordValidationStatus(null);
      return false;
    } finally {
      if (passwordValidationRequestId.current === requestId) {
        setIsPasswordChecking(false);
      }
    }
  };

  const handleEmailChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setEmail(value);
    validateField("email", value);
  };

  const handlePasswordChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const value = e.target.value;
    setPassword(value);
    void validatePasswordField(value);
  };

  const handlerSubmit = async () => {
    const emailValid = validateField("email", email);
    const passwordValid = await validatePasswordField(password);

    if (!emailValid || !passwordValid) {
      showToast("Please fix all validation errors before submitting", "error");
      return;
    }

    setIsLoading(true);

    try {
      // Execute reCAPTCHA verification
      if (!executeRecaptcha) {
        showToast("reCAPTCHA not ready. Please try again.", "error");
        return;
      }
      setIsLoading(true);
      // Execute reCAPTCHA verification (token використовується для валідації на бекенді через middleware)
      await executeRecaptcha("signup");

      // Proceed with registration
      const result = await registerUser(
        email,
        password,
        referralId ? referralId : undefined
      );

      if (result.success) {
        // Store user data in localStorage
        localStorage.setItem("user", JSON.stringify(result.user));
        // Зберігаємо інформацію про те, що користувач реєструвався
        localStorage.setItem("hasRegisteredOrLoggedIn", "true");

        const sp = new URLSearchParams(location.search);
        const u = result.user as { id?: string; userType?: { title?: string } };
        const role = u?.userType?.title?.toLowerCase?.() || "guest";
        // Make sure distinct_id is "real" before signup_completed
        aliasMixpanelUser(u?.id);
        identifyMixpanelUser(u?.id);
        setMixpanelPeopleProfile({ email, name: null });
        trackSignupCompleted({
          user_id: u?.id,
          user_role: role === "viewer" || role === "creator" ? role : "guest",
          signup_method: "email_password",
          source: sp.get("utm_source") || undefined,
          medium: sp.get("utm_medium") || undefined,
          campaign: sp.get("utm_campaign") || undefined,
        });
        if (referralId) {
          trackReferralAttributedStage({
            referral_stage: "signup",
            referrer_user_id: referralId,
            user_id: u?.id,
            user_role: role === "viewer" || role === "creator" ? role : "guest",
          });
        }

        showToast(result.message || "User registered successfully!");

        if (isStagingCreatorOnboardingBypassEnabled()) {
          history.push("/choose-user-type");
        } else {
          history.push("/email-activation", { userData: result.user });
        }
      } else {
        showToast("Registration failed. Please try again.", "error");
      }
    } catch (err: any) {
      // Якщо помилка прийшла як рядок з registerUser (з services.js)
      if (typeof err === "string") {
        showToast(err, "error");
        return;
      }

      // Перевіряємо повідомлення від сервера з response.data.message
      // Може бути структура {success: false, message: "..."} в response.data
      const serverMessage =
        err?.response?.data?.message || err?.response?.message;

      if (serverMessage) {
        // Якщо є повідомлення від сервера, використовуємо його
        showToast(serverMessage, "error");
      } else if (
        err.code === "auth/email-already-in-use" ||
        err.customData?._tokenResponse?.error?.message === "EMAIL_EXISTS"
      ) {
        showToast("This email already in use!", "error");
      } else if (err.message?.includes("reCAPTCHA")) {
        showToast("reCAPTCHA verification failed. Please try again.", "error");
      } else {
        // Якщо немає повідомлення від сервера, використовуємо загальне повідомлення
        showToast(err?.message || "Error while registering user!", "error");
      }
    } finally {
      setIsLoading(false);
    }
  };

  // if (user) {
  //   return <Redirect to="/" />;
  // }

  return (
    <>
      <StandardPageHelmet seo={seo} />
      <div className={styles.authWrapper}>
        <div className={styles.registerDesktopContainer}>
          <div className={styles.registerFormContainer}>
            <div className={styles.authLeft}>
              <div className={styles.authBox}>
                <div className={styles.logoContainer}>
                  <img src={Logo} alt="SugarCribs" className={styles.logo} />
                </div>
                <h2 className={styles.title}>Sign up</h2>
                {/* <input
              type="text"
              placeholder="Name"
              value={name}
              onChange={handleNameChange}
              className={styles.inputField}
            /> */}
                {errors.name && (
                  <Typography
                    style={{ fontSize: "11px", color: "#ff4444", marginTop: "4px" }}
                  >
                    {errors.name}
                  </Typography>
                )}

                <label htmlFor="email">
                  <span
                    style={{
                      display: "block",
                      textAlign: "left",
                      marginBottom: 8,
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#ffffff",
                      fontFamily: "TT Norms, sans-serif",
                    }}
                  >
                    E-mail
                  </span>
                  <input
                    id="email"
                    type="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={handleEmailChange}
                    onKeyPress={handleKeyPress}
                    className={styles.inputField}
                  />
                </label>
                {errors.email && (
                  <Typography
                    style={{
                      fontSize: "14px",
                      color: "#fff",
                      marginTop: "4px",
                      textAlign: "left",
                      marginBottom: 24,
                    }}
                  >
                    {errors.email}
                  </Typography>
                )}
                <label htmlFor="password">
                  <span
                    style={{
                      display: "block",
                      textAlign: "left",
                      marginBottom: 8,
                      fontSize: "14px",
                      fontWeight: "500",
                      color: "#ffffff",
                      fontFamily: "TT Norms, sans-serif",
                    }}
                  >
                    Password
                  </span>
                  <div style={{ position: "relative" }}>
                    <input
                      id="password"
                      type={showPassword ? "text" : "password"}
                      placeholder="Password"
                      value={password}
                      onChange={handlePasswordChange}
                      onKeyPress={handleKeyPress}
                      className={styles.inputField}
                      style={{ paddingRight: "50px" }}
                    />
                    <button
                      type="button"
                      onClick={togglePasswordVisibility}
                      style={{
                        position: "absolute",
                        right: "12px",
                        top: "50%",
                        transform: "translateY(-50%)",
                        background: "none",
                        border: "none",
                        cursor: "pointer",
                        color: "#666",
                        padding: "4px",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                      }}
                    >
                      {showPassword ? (
                        <Visibility style={{ fontSize: "24px" }} />
                      ) : (
                        <VisibilityOff style={{ fontSize: "24px" }} />
                      )}
                    </button>
                  </div>
                </label>
                {errors.password && (
                  <Typography
                    style={{
                      fontSize: "14px",
                      color: "#fff",
                      marginTop: "4px",
                      textAlign: "left",
                    }}
                  >
                    {errors.password}
                  </Typography>
                )}
                {/* <Typography style={{ fontSize: "11px" }}>
              Password must be at least 8 characters and include uppercase,
              lowercase, number, and special character.
            </Typography> */}

                <div className={styles.checkboxContainer}>
                  <div className={styles.checkboxItem}>
                    <input
                      type="checkbox"
                      id="age-confirm"
                      checked={ageConfirmed}
                      onChange={(e) => setAgeConfirmed(e.target.checked)}
                      className={styles.checkbox}
                    />
                    <label htmlFor="age-confirm" className={styles.checkboxLabel}>
                      By creating an account, you confirm that you are <span style={{ fontWeight: 700 }}>at least eighteen (18) years of age</span>.
                    </label>
                  </div>
                  <div className={styles.checkboxItem}>
                    <input
                      type="checkbox"
                      id="terms-accept"
                      checked={termsAccepted}
                      onChange={(e) => setTermsAccepted(e.target.checked)}
                      className={styles.checkbox}
                    />
                    <label htmlFor="terms-accept" className={styles.checkboxLabel}>
                      By registering, you acknowledge that you have read, understood, and agree to the{" "}
                      <a
                        href="/terms-of-service"
                        style={{ color: "#FE5A97", textDecoration: "underline" }}
                      >
                        Terms of Service
                      </a>{" "}
                      and{" "}
                      <a
                        href="/privacy-policy"
                        style={{ color: "#FE5A97", textDecoration: "underline" }}
                      >
                        Privacy Policy
                      </a>.
                    </label>
                  </div>
                </div>

                <button
                  className={styles.actionButton}
                  onClick={handlerSubmit}
                  disabled={!isFormValid() || isLoading || !ageConfirmed || !termsAccepted}
                  style={{
                    marginTop: 24,
                    opacity: !isFormValid() || isLoading || !ageConfirmed || !termsAccepted ? 0.6 : 1,
                    cursor: !isFormValid() || isLoading || !ageConfirmed || !termsAccepted ? "not-allowed" : "pointer",
                  }}
                >
                  {isLoading ? "Signing up..." : "Sign up"}
                </button>

                {/* reCAPTCHA v3 runs invisibly via provider */}
                <button
                  className={styles.googleButton}
                  onClick={() => googleRegistration()}
                >
                  <img src={GoogleIcon} alt="Google" /> Sign up with Google
                </button>
                <p className={styles.switchMode}>
                  Already have an account?{" "}
                  <button
                    className={styles.linkButton}
                    onClick={() => history.push("/login")}
                    style={{
                      fontWeight: 600,
                      color: "#FE5A97",
                      border: "none",
                      background: "none",
                      cursor: "pointer",
                      fontSize: "16px",
                      textDecoration: "underline",
                      fontFamily: "Inter, sans-serif",
                    }}
                  >
                    Sign in
                  </button>
                </p>
              </div>
            </div>
          </div>
          <div className={styles.registerPreviewImage}>
            <img src={RegisterPreviewImage} alt="Welcome to SugarCribs" />
          </div>
        </div>
        <div className={styles.authLeftMobile}>
          <div className={styles.authBox}>
            <div className={styles.logoContainer}>
              <img src={Logo} alt="SugarCribs" className={styles.logo} />
            </div>
            <h2 className={styles.title}>Sign up</h2>
            {errors.name && (
              <Typography
                style={{ fontSize: "11px", color: "#ff4444", marginTop: "4px" }}
              >
                {errors.name}
              </Typography>
            )}

            <label htmlFor="email-mobile">
              <span
                style={{
                  display: "block",
                  textAlign: "left",
                  marginBottom: 8,
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#ffffff",
                  fontFamily: "TT Norms, sans-serif",
                }}
              >
                E-mail
              </span>
              <input
                id="email-mobile"
                type="email"
                placeholder="Enter your email"
                value={email}
                onChange={handleEmailChange}
                onKeyPress={handleKeyPress}
                className={styles.inputField}
              />
            </label>
            {errors.email && (
              <Typography
                style={{
                  fontSize: "14px",
                  color: "#fff",
                  marginTop: "4px",
                  textAlign: "left",
                  marginBottom: 24,
                }}
              >
                {errors.email}
              </Typography>
            )}
            <label htmlFor="password-mobile">
              <span
                style={{
                  display: "block",
                  textAlign: "left",
                  marginBottom: 8,
                  fontSize: "14px",
                  fontWeight: "500",
                  color: "#ffffff",
                  fontFamily: "TT Norms, sans-serif",
                }}
              >
                Password
              </span>
              <div style={{ position: "relative" }}>
                <input
                  id="password-mobile"
                  type={showPassword ? "text" : "password"}
                  placeholder="Password"
                  value={password}
                  onChange={handlePasswordChange}
                  onKeyPress={handleKeyPress}
                  className={styles.inputField}
                  style={{ paddingRight: "50px" }}
                />
                <button
                  type="button"
                  onClick={togglePasswordVisibility}
                  style={{
                    position: "absolute",
                    right: "12px",
                    top: "50%",
                    transform: "translateY(-50%)",
                    background: "none",
                    border: "none",
                    cursor: "pointer",
                    color: "#666",
                    padding: "4px",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  {showPassword ? (
                    <Visibility style={{ fontSize: "24px" }} />
                  ) : (
                    <VisibilityOff style={{ fontSize: "24px" }} />
                  )}
                </button>
              </div>
            </label>
            {errors.password && (
              <Typography
                style={{
                  fontSize: "14px",
                  color: "#fff",
                  marginTop: "4px",
                  textAlign: "left",
                }}
              >
                {errors.password}
              </Typography>
            )}

            <div className={styles.checkboxContainer}>
              <div className={styles.checkboxItem}>
                <input
                  type="checkbox"
                  id="age-confirm-mobile"
                  checked={ageConfirmed}
                  onChange={(e) => setAgeConfirmed(e.target.checked)}
                  className={styles.checkbox}
                />
                <label htmlFor="age-confirm-mobile" className={styles.checkboxLabel}>
                  By creating an account, you confirm that you are <span style={{ fontWeight: 700 }}>at least eighteen (18) years of age</span>.
                </label>
              </div>
              <div className={styles.checkboxItem}>
                <input
                  type="checkbox"
                  id="terms-accept-mobile"
                  checked={termsAccepted}
                  onChange={(e) => setTermsAccepted(e.target.checked)}
                  className={styles.checkbox}
                />
                <label htmlFor="terms-accept-mobile" className={styles.checkboxLabel}>
                  By registering, you acknowledge that you have read, understood, and agree to the{" "}
                  <a
                    href="/terms-of-service"
                    style={{ color: "#FE5A97", textDecoration: "underline" }}
                  >
                    Terms of Service
                  </a>{" "}
                  and{" "}
                  <a
                    href="/privacy-policy"
                    style={{ color: "#FE5A97", textDecoration: "underline" }}
                  >
                    Privacy Policy
                  </a>.
                </label>
              </div>
            </div>

            <button
              className={styles.actionButton}
              onClick={handlerSubmit}
              disabled={!isFormValid() || isLoading || !ageConfirmed || !termsAccepted}
              style={{
                marginTop: 24,
                opacity: !isFormValid() || isLoading || !ageConfirmed || !termsAccepted ? 0.6 : 1,
                cursor: !isFormValid() || isLoading || !ageConfirmed || !termsAccepted ? "not-allowed" : "pointer",
              }}
            >
              {isLoading ? "Signing up..." : "Sign up"}
            </button>

            <button
              className={styles.googleButton}
              onClick={() => googleRegistration()}
            >
              <img src={GoogleIcon} alt="Google" /> Sign up with Google
            </button>
            <p className={styles.switchMode}>
              Already have an account?{" "}
              <button
                className={styles.linkButton}
                onClick={() => history.push("/login")}
                style={{
                  fontWeight: 600,
                  color: "#FE5A97",
                  border: "none",
                  background: "none",
                  cursor: "pointer",
                  fontSize: "16px",
                  textDecoration: "underline",
                  fontFamily: "Inter, sans-serif",
                }}
              >
                Sign in
              </button>
            </p>
          </div>
        </div>
      </div>
    </>
  );
}
export default SignUp;
